import React from 'react';
import { Country, SUPPORTED_COUNTRIES } from '../types';
import { Globe, Check } from 'lucide-react';

interface CountrySelectorProps {
  isOpen: boolean;
  onSelect: (country: Country) => void;
  selectedCountry?: Country;
  isDismissible?: boolean;
  onClose?: () => void;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ 
  isOpen, 
  onSelect, 
  selectedCountry,
  isDismissible = false,
  onClose
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] animate-in fade-in duration-300" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-in zoom-in-95 duration-300">
        <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden relative">
            
            {/* Header */}
            <div className="p-8 pb-4 text-center">
                <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                    <Globe className="w-8 h-8 text-cyan-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Seleccioná tu Jurisdicción</h2>
                <p className="text-gray-400 text-sm">
                    Adaptaremos el análisis legal y las sugerencias a las leyes y prácticas de:
                </p>
            </div>

            {/* List */}
            <div className="px-6 pb-8 space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {SUPPORTED_COUNTRIES.map((country) => {
                    const isSelected = selectedCountry?.code === country.code;
                    return (
                        <button
                            key={country.code}
                            onClick={() => onSelect(country)}
                            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 border group ${
                                isSelected 
                                    ? 'bg-cyan-950/30 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.1)]' 
                                    : 'bg-zinc-800/30 border-zinc-700/50 hover:bg-zinc-800 hover:border-zinc-600'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <img 
                                  src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`} 
                                  alt={country.name}
                                  className="w-10 h-7 object-cover rounded shadow-sm"
                                />
                                <div className="text-left">
                                    <h3 className={`font-bold ${isSelected ? 'text-cyan-400' : 'text-gray-200 group-hover:text-white'}`}>
                                        {country.name}
                                    </h3>
                                    <p className="text-xs text-zinc-500 font-medium">Derecho {country.adjective}</p>
                                </div>
                            </div>
                            {isSelected && (
                                <div className="bg-cyan-500/20 p-1.5 rounded-full">
                                    <Check className="w-4 h-4 text-cyan-400" />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {isDismissible && onClose && (
                <div className="p-4 border-t border-zinc-800 bg-zinc-900/50 text-center">
                    <button 
                        onClick={onClose}
                        className="text-sm text-zinc-500 hover:text-white transition-colors"
                    >
                        Cancelar
                    </button>
                </div>
            )}
        </div>
      </div>
    </>
  );
};

export default CountrySelector;